import {
    backgroundColorData,
    backgroundOpacityData
} from '@mintdata-wsp/common/controllers/surface/animation/BackgroundPropertyData';
import {basicData} from '@mintdata-wsp/common/controllers/surface/animation/BasicPropertyData';
import {backgroundBlurData, blurData} from '@mintdata-wsp/common/controllers/surface/animation/BlurPropertyData';
import {
    borderColorData,
    borderOpacityData
} from '@mintdata-wsp/common/controllers/surface/animation/BorderPropertyData';
import {
    basicLayoutData,
    heightLayoutData,
    layoutData,
    widthLayoutData
} from '@mintdata-wsp/common/controllers/surface/animation/LayoutPropertyData';
import {VisualBoxTraverser} from '@mintdata-wsp/common/controllers/surface/VisualBoxVisitor';
import {
    AnimateTransformProperty,
    AnimateUnitsType,
    IAnimateOptions,
    IAnimateProps,
    IAnimateTarget,
    IAnimationData,
    IAnimationPropertyData,
    IPropData,
    PlaybackType
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/animation/AnimationTypes';
import {
    AnimationValueMap,
    Box,
    IAnimationRef
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {OpacityFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/opacity/OpacityConverter';
import {PerspectiveFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/perspective/PerspectiveConverter';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RotationFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationConverter';
import {ScaleFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scale/ScaleConverter';
import {SkewFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/skew/SkewConverter';
import {AnimateProperty, GetPropPathFunc, GetPropStylerFunc} from '@mintdata/mint-kit/api/animation/AnimationProps';
import {AnimationType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';
import {StringUtil} from '@mintdata/mint-kit/impl/util/StringUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';
import {easing, styler, tween} from 'popmotion/dist/popmotion.js';
import {Styler} from 'stylefire';

export class AnimationController
{
    private _propertiesData:{[property:string]:IAnimationPropertyData} = {
        [AnimateTransformProperty.Left]: basicLayoutData,
        [AnimateTransformProperty.Top]: basicLayoutData,
        [AnimateTransformProperty.Right]: basicLayoutData,
        [AnimateTransformProperty.Bottom]: basicLayoutData,
        [AnimateTransformProperty.Width]: widthLayoutData,
        [AnimateTransformProperty.Height]: heightLayoutData,
        [AnimateTransformProperty.RotateX]: basicData(RotationFieldPaths.ANGLE_X),
        [AnimateTransformProperty.RotateY]: basicData(RotationFieldPaths.ANGLE_Y),
        [AnimateTransformProperty.RotateZ]: basicData(RotationFieldPaths.ANGLE_Z),
        [AnimateTransformProperty.Scale]: basicData(ScaleFieldPaths.SCALE, null, 1),
        [AnimateTransformProperty.ScaleX]: basicData(ScaleFieldPaths.SCALE_X, null, 1),
        [AnimateTransformProperty.ScaleY]: basicData(ScaleFieldPaths.SCALE_Y, null, 1),
        [AnimateTransformProperty.ScaleZ]: basicData(ScaleFieldPaths.SCALE_Z, null, 1),
        [AnimateTransformProperty.Opacity]: basicData(OpacityFieldPaths.OPACITY),
        [AnimateTransformProperty.SkewX]: basicData(SkewFieldPaths.SKEW_X),
        [AnimateTransformProperty.SkewY]: basicData(SkewFieldPaths.SKEW_Y),
        [AnimateTransformProperty.TranslateX]: layoutData(AnimateTransformProperty.Left),
        [AnimateTransformProperty.TranslateY]: layoutData(AnimateTransformProperty.Top),
        [AnimateTransformProperty.TranslateZ]: basicData(PerspectiveFieldPaths.TRANSLATE_Z),
        [AnimateTransformProperty.X]: layoutData(AnimateTransformProperty.Left),
        [AnimateTransformProperty.Y]: layoutData(AnimateTransformProperty.Top),

        [AnimateProperty.BackgroundColor]: backgroundColorData,
        [AnimateProperty.BackgroundOpacity]: backgroundOpacityData,
        [AnimateProperty.BorderColor]: borderColorData,
        [AnimateProperty.BorderOpacity]: borderOpacityData,
        [AnimateProperty.Color]: null,
        [AnimateProperty.Blur]: blurData,
        [AnimateProperty.BackgroundBlur]: backgroundBlurData,
    };

    private _animationFunction = {
        [AnimationType.Tween]: tween,
    };

    private _transformProperties:Array<string> = [
        AnimateTransformProperty.Left,
        AnimateTransformProperty.Top,
        AnimateTransformProperty.Right,
        AnimateTransformProperty.Bottom,
        AnimateTransformProperty.Width,
        AnimateTransformProperty.Height,
        AnimateTransformProperty.RotateX,
        AnimateTransformProperty.RotateY,
        AnimateTransformProperty.RotateZ,
        AnimateTransformProperty.Scale,
        AnimateTransformProperty.ScaleX,
        AnimateTransformProperty.ScaleY,
        AnimateTransformProperty.ScaleZ,
        AnimateTransformProperty.Opacity,
        AnimateTransformProperty.SkewX,
        AnimateTransformProperty.SkewY,
        AnimateTransformProperty.TranslateX,
        AnimateTransformProperty.TranslateY,
        AnimateTransformProperty.TranslateZ,
        AnimateTransformProperty.X,
        AnimateTransformProperty.Y
    ];

    _valueFormatters = {
        [AnimateUnitsType.None]: (value:string | number):string | number => value,
        [AnimateUnitsType.Number]: (value:string | number):string | number => value,
        [AnimateUnitsType.Pixels]: (value:string | number):string | number => `${value}px`,
        [AnimateUnitsType.Percentages]: (value:string | number):string | number => `${value}%`,
        [AnimateUnitsType.Fractions]: (value:string | number):string | number => `${value}fr`
    };

    private _findAnimateTargets(box:Box, repeaterLevels:Array<number>):Array<IAnimateTarget>
    {
        const animateTargets:Array<IAnimateTarget> = [];

        let currentRepeaterIndex:number = repeaterLevels ? repeaterLevels.shift() : null;

        VisualBoxTraverser.visit(
            box.page.artboard.visualBoxes,
            {
                onVisualBox: (visualBox:VisualBox, repeater:Repeater):boolean =>
                {
                    if( visualBox.box.id === box.id )
                    {
                        if( currentRepeaterIndex != null && !repeaterLevels.length )
                        {
                            if( repeater.index === currentRepeaterIndex )
                            {
                                animateTargets.push({visualBox, repeater});

                                return true;
                            }
                        }
                        else
                            animateTargets.push({visualBox, repeater});
                    }

                    return false;
                }
            }
        );

        return animateTargets;
    }

    @bind
    private _iValueToPrimitive(value:IValue):string | number | boolean
    {
        if( Type.isInstanceOf(value, Type.Integer)
            || Type.isInstanceOf(value, Type.Number)
            || Type.isInstanceOf(value, Type.String)
            || Type.isInstanceOf(value, Type.Boolean) )

            return (value as any).value;

        return null;
    }

    private _getCssProperty(property:string):string
    {
        const animationPropData:IAnimationPropertyData = this._propertiesData[property];
        return (animationPropData && animationPropData.cssProperty) || property;
    }

    private _processOptionsFrame(options:IFrame):IAnimateOptions
    {
        const animationOptions:IAnimateOptions = {
            props: {
                duration: 500,
                ease: easing.easeInOut,
                from: {},
                to: {}
            },
            isAbsoluteValues: true,
            isPropsSeeInitialValues: false,
            isKeepFinalValues: true,
            isDisabled: false,
            delay: 0,
            invalidProp: null,
            isToFinal: true
        };
        let playbackType:PlaybackType = PlaybackType.Flip;
        let iterations:number = null;

        options.getRecords().forEach((record:IFrameRecord) =>
        {
            const cells:Array<string | number | boolean> = record.getCells().map(this._iValueToPrimitive);

            if( cells[0] != null )
            {
                const cellString:string = cells[0].toString();
                const optionKey:string = StringUtil.loDashToCamelCase(
                    cellString.indexOf('_') !== -1
                        ? cellString.toLowerCase()
                        : cellString
                );

                switch( optionKey )
                {
                    case 'absoluteValues':
                        if( cells[1] === false )
                            animationOptions.isAbsoluteValues = false;
                        break;
                    case 'propsSeeInitialValues':
                        if( cells[1] )
                            animationOptions.isPropsSeeInitialValues = true;
                        break;
                    case 'keepFinalValues':
                        if( cells[1] === false )
                            animationOptions.isKeepFinalValues = false;
                        break;
                    case 'delay':
                        if( cells[1] != null )
                            animationOptions.delay = cells[1] as number;
                        break;
                    case 'duration':
                        if( cells[1] != null )
                            animationOptions.props.duration = cells[1] as number;
                        break;
                    case 'playback':
                        if( cells[1] != null )
                        {
                            playbackType = cells[1] as PlaybackType;

                            if( iterations === null )
                                iterations = Infinity;
                        }
                        break;
                    case 'iterations':
                        if( cells[1] != null )
                        {
                            const cellValueString:string = cells[1].toString().toLowerCase();
                            const count:number = parseFloat(cellValueString);

                            if( !isNaN(count) )
                            {
                                iterations = count - 1;

                                if( iterations < 0 )
                                    animationOptions.isDisabled = true;
                            }
                            else if( cellValueString === 'infinity' || cellValueString === 'infinite' )
                                iterations = Infinity;
                        }
                        break;
                    case 'ease':
                        if( cells[1] != null )
                        {
                            const easingString:string = StringUtil.loDashToCamelCase(cells[1].toString());

                            if( easingString && easing[easingString] )
                                animationOptions.props.ease = easing[easingString];
                        }
                        break;
                    default:
                        if( cellString.startsWith('_') )
                            break;

                        const property:string = optionKey;

                        if( !Object.values(AnimateTransformProperty).includes(property)
                            && !Object.values(AnimateProperty).includes(property) )
                        {
                            animationOptions.invalidProp = property;
                            break;
                        }

                        if( cells[1] != null && cells[2] != null )
                        {
                            animationOptions.props.from[property] = cells[1] as any;
                            animationOptions.props.to[property] = cells[2] as any;
                        }
                        else if( cells[1] != null )
                        {
                            animationOptions.props.from[property] = null;
                            animationOptions.props.to[property] = cells[1] as any;
                        }

                        break;
                }
            }
        });

        if( playbackType && iterations != null )
        {
            animationOptions.isToFinal = iterations % 2 === 0;
            animationOptions.props[playbackType] = iterations || Infinity;
        }

        return animationOptions;
    }

    private _getPropDataByPropertyName(animateTarget:IAnimateTarget,
                                       animationData:IAnimationData,
                                       property:string):IPropData
    {
        const {visualBox: {box: {componentDesc}}} = animateTarget;

        let prop:Prop<any>;
        let propPathString:string;

        if( this._transformProperties.includes(property) )
        {
            if( !componentDesc.isSelfDirected )
                switch( property )
                {
                    case AnimateTransformProperty.Width:
                    case AnimateTransformProperty.Height:
                        prop = componentDesc.layoutProp;
                        break;
                }

            if( !prop )
                switch( property )
                {
                    case AnimateTransformProperty.Left:
                    case AnimateTransformProperty.X:
                    case AnimateTransformProperty.TranslateX:
                        prop = componentDesc.layoutLeftProp;
                        break;
                    case AnimateTransformProperty.Top:
                    case AnimateTransformProperty.Y:
                    case AnimateTransformProperty.TranslateY:
                        prop = componentDesc.layoutTopProp;
                        break;
                    case AnimateTransformProperty.Right:
                        prop = componentDesc.layoutRightProp;
                        break;
                    case AnimateTransformProperty.Bottom:
                        prop = componentDesc.layoutBottomProp;
                        break;
                    case AnimateTransformProperty.Width:
                        prop = componentDesc.layoutWidthProp;
                        break;
                    case AnimateTransformProperty.Height:
                        prop = componentDesc.layoutHeightProp;
                        break;
                    case AnimateTransformProperty.RotateX:
                    case AnimateTransformProperty.RotateY:
                    case AnimateTransformProperty.RotateZ:
                        prop = componentDesc.rotationProp;
                        break;
                    case AnimateTransformProperty.Scale:
                    case AnimateTransformProperty.ScaleX:
                    case AnimateTransformProperty.ScaleY:
                    case AnimateTransformProperty.ScaleZ:
                        prop = componentDesc.scaleProp;
                        break;
                    case AnimateTransformProperty.Opacity:
                        prop = componentDesc.opacityProp;
                        break;
                    case AnimateTransformProperty.SkewX:
                    case AnimateTransformProperty.SkewY:
                        prop = componentDesc.skewProp;
                        break;
                    case AnimateTransformProperty.TranslateZ:
                        prop = componentDesc.perspectiveProp;
                        break;
                }

            const animationPropData:IAnimationPropertyData = this._propertiesData[property];

            if( animationPropData )
                if( typeof animationPropData.propertyPath === 'function' )
                    propPathString = animationPropData.propertyPath(animateTarget);
                else
                    propPathString = animationPropData.propertyPath;
        }
        else
        {
            prop = animateTarget.visualBox.box.componentDesc.properties.get('model');
            propPathString = animationData.getPropPath(property);
        }

        return {
            prop,
            propPath: propPathString ? PropPath.fromString(propPathString) : null
        };
    }

    private _calculateRelativeValues(animateTarget:IAnimateTarget,
                                     animationData:IAnimationData,
                                     animateOptions:IAnimateOptions)
    {
        Object.keys(animateOptions.props.from).forEach((property:string) =>
        {
            const propData:IPropData = this._getPropDataByPropertyName(animateTarget, animationData, property);

            if( !propData.prop || !propData.propPath )
                return;

            const value:IValue = propData.prop.getRepeatedValueByPath(animateTarget.repeater, propData.propPath);

            let fromValue:number = parseFloat(animateOptions.props.from[property] as any);
            let toValue:number = parseFloat(animateOptions.props.to[property] as any);

            if( !isNaN(fromValue) && !isNaN(toValue) )
            {
                if( Type.isInstanceOf(value, Type.Number)
                    || Type.isInstanceOf(value, Type.Integer)
                    || Type.isInstanceOf(value, Type.String) )
                {
                    const delta:number = parseFloat(this._iValueToPrimitive(value) as any);

                    if( isNaN(delta) )
                        return;

                    fromValue += delta;
                    toValue += delta;
                }

                animateOptions.props.from[property] = fromValue;
                animateOptions.props.to[property] = toValue;
            }
        });
    }

    private _preprocessPropertyValue(animateTarget:IAnimateTarget,
                                     animationData:IAnimationData,
                                     property:string,
                                     value:number | string,
                                     isStoreUnitsType:boolean = false):number | string
    {
        if( typeof value === 'string' )
        {
            let isUpdated:boolean = false;

            const replaceParam = (value:string, replaceParam:string, param:string):string =>
            {
                if( value.indexOf(`$${replaceParam}`) !== -1 )
                {
                    const paramValue:string = animationData.transform.get(param, true);
                    value = value.replace(new RegExp(`\\$${replaceParam}`, 'gi'), paramValue);

                    isUpdated = true;
                }

                return value;
            };

            value = replaceParam(value, 'width', 'width');
            value = replaceParam(value, 'height', 'height');
            value = replaceParam(value, 'left', 'left');
            value = replaceParam(value, 'x', 'left');
            value = replaceParam(value, 'top', 'top');
            value = replaceParam(value, 'y', 'top');

            if( isUpdated )
                value = eval(value);
        }
        else if( value == null )
        {
            const propData:IPropData = this._getPropDataByPropertyName(animateTarget, animationData, property);

            if( !propData.prop || !propData.propPath )
                return;

            const propValue = propData.prop.getRepeatedValueByPath(animateTarget.repeater, propData.propPath);
            const animationPropData:IAnimationPropertyData = this._propertiesData[property];

            value = propValue ? propValue.value : (animationPropData && animationPropData.defaultFromValue) || 0;
        }

        const animateUnitsType:AnimateUnitsType = typeof value === 'number'
            ? AnimateUnitsType.Number
            : /\d+%$/.test(value)
                ? AnimateUnitsType.Percentages
                : /\d+px$/i.test(value)
                    ? AnimateUnitsType.Pixels
                    : /\d+fr$/i.test(value)
                        ? AnimateUnitsType.Fractions
                        : AnimateUnitsType.None;

        if( isStoreUnitsType )
            animationData.units[property] = animateUnitsType;

        if( animateUnitsType !== AnimateUnitsType.None && animateUnitsType !== AnimateUnitsType.Number )
            value = parseFloat(value as string);

        return value;
    }

    private _preprocessAnimationProps(animateTarget:IAnimateTarget,
                                      animationData:IAnimationData,
                                      animateProps:IAnimateProps):void
    {
        Object.keys(animateProps.from).forEach((property:string) =>
        {
            animateProps.from[property] = this._preprocessPropertyValue(
                animateTarget,
                animationData,
                property,
                animateProps.from[property],
                true
            );
        });

        Object.keys(animateProps.to).forEach((property:string) =>
        {
            animateProps.to[property] = this._preprocessPropertyValue(
                animateTarget,
                animationData,
                property,
                animateProps.to[property]
            );
        });
    }

    @action
    private _applyValuesToProps(animateTarget:IAnimateTarget,
                                animationData:IAnimationData,
                                animateOptions:IAnimateOptions,
                                isStartValues:boolean = false)
    {
        const {visualBox: {box: {componentDesc}}} = animateTarget;
        const values:AnimationValueMap = isStartValues ? animateOptions.props.from : animateOptions.props.to;

        Object.keys(values).forEach((property:string) =>
        {
            const animationPropData:IAnimationPropertyData = this._propertiesData[property];
            const propData:IPropData = this._getPropDataByPropertyName(animateTarget, animationData, property);
            const animateUnitsType:AnimateUnitsType = animationData.units[property] || AnimateUnitsType.None;
            const value:string | number = this._valueFormatters[animateUnitsType](values[property]);

            if( animationPropData && animationPropData.relatedPropsUpdater )
                animationPropData.relatedPropsUpdater(animationData, propData, value);

            if( propData.prop && propData.propPath )
            {
                if( propData.prop.isBoundPath(propData.propPath) )
                {
                    componentDesc.applyAnimationFinalValueToProp(
                        propData.prop,
                        propData.propPath,
                        value,
                        animateTarget.repeater
                    );
                }
                else
                    componentDesc.changePropValue(propData.prop, propData.propPath, value);
            }
        });
    }

    private _storeInitialValues(animationData:IAnimationData, animateOptions:IAnimateOptions)
    {
        animationData.initialValues = {};

        Object.keys(animateOptions.props.from).forEach((property:string) =>
        {
            const cssProperty:string = this._getCssProperty(property);

            if( this._transformProperties.includes(property) )
                animationData.initialValues[property] = animationData.transform.get(cssProperty, true);
            else
            {
                const styler:Styler = animationData.getPropStyler(property);

                if( styler )
                    animationData.initialValues[property] = styler.get(cssProperty, true);
            }
        });
    }

    private _restoreInitialValues(animationData:IAnimationData)
    {
        const stylers:Set<Styler> = new Set<Styler>();

        Object.keys(animationData.initialValues).forEach((property:string) =>
        {
            const cssProperty:string = this._getCssProperty(property);
            const styler:Styler = this._transformProperties.includes(property)
                ? animationData.transform
                : animationData.getPropStyler(property);

            if( styler )
            {
                styler.set(cssProperty, animationData.initialValues[property]);
                stylers.add(styler);
            }
        });

        animationData.initialValues = {};

        stylers.forEach((styler:Styler) => styler.render());
    }

    private _onAnimationUpdate(animateTarget:IAnimateTarget,
                               animationData:IAnimationData,
                               properties:AnimationValueMap):void
    {
        Object.keys(properties).forEach((property:string) =>
        {
            const cssProperty:string = this._getCssProperty(property);
            const animationPropData:IAnimationPropertyData = this._propertiesData[property];
            const styler:Styler = this._transformProperties.includes(property)
                ? animationData.transform
                : animationData.getPropStyler(property);
            const animateUnitsType:AnimateUnitsType = animationData.units[property] || AnimateUnitsType.None;
            const value:string | number = this._valueFormatters[animateUnitsType](properties[property]);

            if( animationPropData && animationPropData.setter )
                animationPropData.setter(
                    animateTarget,
                    animationData,
                    styler,
                    property,
                    cssProperty,
                    value
                );
            else if( styler )
                styler.set(cssProperty, value);
        });
    }

    private _onAnimationComplete(animateTarget:IAnimateTarget,
                                 animationData:IAnimationData,
                                 animateOptions:IAnimateOptions,
                                 animationRef:IAnimationRef):void
    {
        animationRef.promiseResolver(true);

        const animationRefIndex:number = animationData.animationRefs.indexOf(animationRef);

        if( animationRefIndex !== -1 )
            animationData.animationRefs.splice(animationRefIndex, 1);

        if( animationData.animationRefs.length === 0 )
        {
            animationData.isInProgress = false;

            if( animateOptions.isKeepFinalValues )
                requestAnimationFrame(() =>
                {
                    this._applyValuesToProps(
                        animateTarget,
                        animationData,
                        animateOptions,
                        !animateOptions.isToFinal
                    );
                });
            else
                this._restoreInitialValues(animationData);
        }
    }

    @bind
    private async _animateTarget(animateTarget:IAnimateTarget,
                                 animationData:IAnimationData,
                                 animationType:AnimationType,
                                 animateOptions:IAnimateOptions):Promise<boolean>
    {
        if( !animationData.transform && animationData.transformRef )
            animationData.transform = styler(animationData.transformRef);

        const animationFunc = this._animationFunction[animationType];

        if( animationFunc && animationData.transform )
        {
            return new Promise<boolean>(resolve =>
            {
                const delayFunc = animateOptions.delay > 0
                    ? setTimeout
                    : (callback, delay:number) => callback();

                delayFunc(() =>
                {
                    this._preprocessAnimationProps(
                        animateTarget,
                        animationData,
                        animateOptions.props
                    );

                    if( !animateOptions.isAbsoluteValues )
                        this._calculateRelativeValues(animateTarget, animationData, animateOptions);

                    if( animateOptions.isPropsSeeInitialValues )
                        this._applyValuesToProps(animateTarget, animationData, animateOptions, true);

                    requestAnimationFrame(() =>
                    {
                        if( !animationData.isInProgress )
                            this._storeInitialValues(animationData, animateOptions);

                        animationData.isInProgress = true;

                        const animationRef:IAnimationRef = {
                            animation: null,
                            promiseResolver: resolve
                        };

                        animationData.animationRefs.push(animationRef);

                        animationRef.animation = animationFunc(animateOptions.props).start(
                            {
                                update: (properties:AnimationValueMap) =>
                                {
                                    this._onAnimationUpdate(animateTarget, animationData, properties);
                                },
                                complete: () =>
                                {
                                    this._onAnimationComplete(
                                        animateTarget,
                                        animationData,
                                        animateOptions,
                                        animationRef
                                    );
                                }
                            });
                    });

                }, animateOptions.delay);
            });
        }

        return false;
    }

    @action
    public async animate(box:Box,
                         repeaterLevels:Array<number>,
                         animationType:AnimationType,
                         options:IFrame):Promise<boolean | Error>
    {
        const animateTargets:Array<IAnimateTarget> = this._findAnimateTargets(box, repeaterLevels);
        const animateOptions:IAnimateOptions = this._processOptionsFrame(options);

        if( animateOptions.invalidProp )
            return new Error(`#INVALID_ANIMATION_SPEC_KEY: ${animateOptions.invalidProp}`);

        if( animateOptions.isDisabled )
            return false;

        const promises:Array<Promise<boolean>> = animateTargets.map((animateTarget:IAnimateTarget) =>
        {
            const animationData:IAnimationData = animateTarget.visualBox.box.uiState.animationData.get(
                animateTarget.repeater.key
            );

            return this._animateTarget(
                animateTarget,
                animationData,
                animationType,
                animateOptions
            );
        });

        const result:Array<boolean> = await Promise.all(promises);

        return result.every((value:boolean) => value);
    }

    public stopAnimations(box:Box, repeaterLevels:Array<number>):void
    {
        const animateTargets:Array<IAnimateTarget> = this._findAnimateTargets(box, repeaterLevels);

        animateTargets.forEach((animateTarget:IAnimateTarget) =>
        {
            const animationData:IAnimationData = animateTarget.visualBox.box.uiState.animationData.get(
                animateTarget.repeater.key
            );

            animationData.animationRefs.forEach((animationRef:IAnimationRef) =>
            {
                animationRef.animation.stop();
                animationRef.promiseResolver(false);
            });

            animationData.isInProgress = false;
            animationData.animationRefs = [];

            this._restoreInitialValues(animationData);
        });
    }

    public registerComponentPropStylerFunc(visualBox:VisualBox,
                                           repeater:Repeater,
                                           getPropStylerFunc:GetPropStylerFunc):void
    {
        if( !visualBox.box.uiState.animationData.has(repeater.key) )
            visualBox.box.uiState.animationData.set(
                repeater.key,
                {
                    animationRefs: [],
                    units: {},
                    getPropStyler: getPropStylerFunc
                }
            );
        else
            visualBox.box.uiState.animationData.get(repeater.key).getPropStyler = getPropStylerFunc;
    }

    public registerComponentPropPathFunc(visualBox:VisualBox,
                                         repeater:Repeater,
                                         getPropPathFunc:GetPropPathFunc):void
    {
        if( !visualBox.box.uiState.animationData.has(repeater.key) )
            visualBox.box.uiState.animationData.set(
                repeater.key,
                {
                    animationRefs: [],
                    units: {},
                    getPropPath: getPropPathFunc
                }
            );
        else
            visualBox.box.uiState.animationData.get(repeater.key).getPropPath = getPropPathFunc;
    }

    public registerTransformRef(visualBox:VisualBox, repeater:Repeater, transformRef:HTMLElement):void
    {
        if( !visualBox.box.uiState.animationData.has(repeater.key) )
            visualBox.box.uiState.animationData.set(
                repeater.key,
                {
                    animationRefs: [],
                    units: {},
                    transformRef
                }
            );
        else
            visualBox.box.uiState.animationData.get(repeater.key).transformRef = transformRef;
    }

    public unregisterTransformRef(visualBox:VisualBox, repeater:Repeater):void
    {
        visualBox.box.uiState.animationData.delete(repeater.key);
    }
}
